/**
 * Канон user-facing copy для org tariff lifecycle (ADR-0019): shell, полоса «только просмотр», «Мой тариф».
 */

export const ORG_LIFECYCLE_PRIMARY_CTA_LABEL = 'Тариф организации' as const;

export const ORG_LIFECYCLE_SUPPORT_CTA_LABEL = 'Поддержка';

/** Согласован shell ↔ «Мой тариф» ↔ полоса «только просмотр»: не про модули пакета, а про срок/оплату. */
export const ORG_LIFECYCLE_CANONICAL_PRIMARY_CTA_EXPLANATION = `Сейчас ведущее действие — открыть «${ORG_LIFECYCLE_PRIMARY_CTA_LABEL}» (та же кнопка, что в жёлтом баннере вверху). Это путь к оплате, продлению и смене плана, а не к «включению модуля» в «Организации».`;

export const ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_ORG_GRACE =
  'Когда оплата будет подтверждена, жёлтый блок исчезнет и ограничения на изменения в CRM снимутся. Если баннер не исчез сразу — обновите страницу.';

export const ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_POST_GRACE =
  'После оплаты или смены плана баннер исчезнет, а режим «только просмотр» по тарифу снимется. Если сценарий не меняется — «Поддержка» или обновление страницы.';

export const ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_LIMIT_GRACE =
  'Чтобы снова работать в штатном режиме лимитов, смените тариф до конца льготы по квотам или снизьте нагрузку; после смены плана предупреждение уйдёт.';

export const ORG_LIFECYCLE_QUOTA_LIMIT_BANNER_TITLE = 'Льготный период по квотам тарифа';

export function orgLifecycleQuotaLimitBannerBody(graceUntilFormatted: string, extraNear: string): string {
  return (
    `Лимиты почти исчерпаны: контрольная дата — ${graceUntilFormatted}.` +
    ` Это предупреждение, а не блокировка: CRM продолжает работать. Проверьте численность сотрудников, клиентов и филиалов.` +
    ` Смена тарифа или снижение нагрузки — в разделе «${ORG_LIFECYCLE_PRIMARY_CTA_LABEL}».` +
    extraNear
  );
}

export const ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_NEEDS_PAYMENT =
  'После оплаты или назначения плана баннер исчезнет — так же, как по кнопке «Тариф организации» выше.';

export const ORG_LIFECYCLE_TARIFF_PAGE_RECOVERY_CHECKLIST = `1) «${ORG_LIFECYCLE_PRIMARY_CTA_LABEL}» (как в баннере) → оплата или смена плана. 2) Дождаться снятия жёлтого баннера вверху (и полосы только просмотр, если она отображается). 3) При залипании сигнала — «${ORG_LIFECYCLE_SUPPORT_CTA_LABEL}».`;

export const ORG_LIFECYCLE_STRIP_ALONG_BANNER_NOTE =
  'Если вверху нет жёлтого блока с датой — данные могли ещё не загрузиться. Запрет на изменения в CRM совпадает с жёлтым баннером, когда тот отображается.';

export const ORG_LIFECYCLE_BANNER_NOT_A_MODULE_SETTING =
  'Это про срок подписки, а не про настройки модулей в «Организации».';

export const ORG_LIFECYCLE_READ_ONLY_STRIP_FALLBACK =
  'Изменение данных в CRM временно недоступно. Просмотр разделов и отчётов — без ограничений. Продлите подписку в «Тариф организации»; после подтверждения оплаты ограничение снимется. Если сценарий не сменился сразу — обновите страницу.';

export const ORG_LIFECYCLE_NORMAL_MODE_SUMMARY_LINE =
  'Ограничений по сроку подписки сейчас нет. Напоминания о сроке — в баннере вверху, если ситуация изменится.';

export const ORG_LIFECYCLE_MUTATION_HINT_NO_DETAIL =
  'Изменения по организации сейчас ограничены по сроку подписки. Смотрите жёлтый баннер вверху; оплату и смену плана откройте в «Тариф организации».';

export const ORG_LIFECYCLE_MUTATION_HINT_STATUS_ONLY =
  'После окончания оплаченного периода часть действий сотрудников временно недоступна («только просмотр»). Ориентир — баннер вверху; не путайте это с отключённым модулем в настройках.';

export const ORG_LIFECYCLE_GRACE_SUMMARY_SUFFIX =
  'детали, срок окончания и ограничения — в жёлтом баннере вверху.';

export const ORG_LIFECYCLE_SUMMARY_POST_GRACE =
  'Льготный период закончился, подписку нужно оплатить. Оплатите или смените план в этом разделе — как по кнопке в баннере. Кнопки в таблице снова станут доступны после подтверждения оплаты; жёлтый баннер исчезнет, когда срок оплаты обновится.';

export const ORG_LIFECYCLE_SUMMARY_NEEDS_PAYMENT =
  'По подписке требуется оплата или выбор тарифа — жёлтый баннер вверху и таблица тарифов ниже.';

export const ORG_LIFECYCLE_TABLE_ACTION_DISABLED_FALLBACK =
  'Смена тарифа недоступна из-за срока подписки. См. жёлтый баннер вверху; после оплаты или смены плана действия снова активны.';

export function tariffPlanActionDisabledTitle(
  orgTariffWrite: { allowed: boolean; detail?: string | null } | null | undefined
): string {
  const d = (orgTariffWrite?.detail ?? '').trim();
  if (d) return d;
  return ORG_LIFECYCLE_TABLE_ACTION_DISABLED_FALLBACK;
}
